import { BlurEvent, Button, ClickEvent, DataSourceExecutionEvent, DataSourceMode, EditRowDecorator, Label, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Panel, PrintableEvent, Snackbar, Table, TableColumn, TableRow, TableRowDisplayEvent, Textbox, Toast } from "@mcleod/components";
import { AutogenLayoutAvailableLoadsList } from "@mcleod/crm/src/autogen/AutogenLayoutAvailableLoadsList";
import { Api, Currency, DateRange, DisplayType, DisplayValue, HorizontalAlignment, ModelRow } from "@mcleod/core";
import { CommonTooltips } from "@mcleod/common";
import { CarrierOfferInfo, OfferSource, OrderTabView, QuickOfferSlideout, QuickSellDetails } from "@mcleod/dispatch";
import { CarrierAssignmentStepper } from "@mcleod/powerbroker/src/CarrierAssignmentStepper";
import { ModelCarrier, RowCarrier } from "@mcleod/dispatch/src/models/ModelCarrier";
import { LMEGeneralSettings, LMEUserSettings } from "@mcleod/general";
import { OfferField } from "@mcleod/powerbroker/src/models/ModelCarrierOffer";
import { MovementCarrierPay } from "@mcleod/powerbroker/src/MovementCarrierPay";

export class AvailableLoadsList extends AutogenLayoutAvailableLoadsList {

    private _carrierId: string = null;
    private carrier: RowCarrier = null;
    private _contactFilter: (event: LookupModelSearchEvent) => void = null;

    sourcePnnControlAfterExecution(event: DataSourceExecutionEvent) {
        const defaultDateRange = DateRange.parseDateRangeWithKeywords(
            `t-${this.sourcePnnControl.activeRow.getNumber("load_days_prior")},` +
            `t+${this.sourcePnnControl.activeRow.getNumber("load_days_after")}`
        );
        this.textboxDateRange.text = DisplayValue.getDisplayValue(defaultDateRange, DisplayType.DATERANGE, this.textboxDateRange.format);
    }

    tableAvailableLoadsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        const labelOrder = tableRow.findComponentById("labelOrder") as Label;
        if (labelOrder) {
            labelOrder.applyLinkStyle();
        }

        const imageHold = tableRow.findComponentById("imageHold");
        if (modelRow.getBoolean("on_hold", false) && imageHold) {
            imageHold.visible = true;
            imageHold.tooltip = (!modelRow.isNull("hold_reason"))
                ? modelRow.getString("hold_reason")
                : "On Hold";
        }

        if (modelRow.getBoolean("brokerage", false)) {
            const imageBrokerage = tableRow.findComponentById("imageBrokerage");
            if (imageBrokerage) {
                imageBrokerage.visible = true;
            }

            const buttonAssign = tableRow.findComponentById("buttonAssign");
            if (buttonAssign) {
                buttonAssign.visible = true;
            }

            const buttonOffer = tableRow.findComponentById("buttonOffer");
            if (buttonOffer) {
                buttonOffer.visible = true;
            }
        } else {
            const checkbox = tableRow.findComponentById("checkboxSelectRow");
            if (checkbox) {
                checkbox.enabled = false;
            }
            tableRow.enabled = false;
        }

        const contactList = modelRow.get("contact_list") as any[];
        if (contactList && contactList.length > 0) {
            const labelContacts = tableRow.findComponentById("labelContacts") as Label;
            if (labelContacts) {
                labelContacts.text = `${contactList.length} contact(s)`;
                this.buildContactTooltip(labelContacts, contactList);
            }
        }
    }

    buttonAssignCarrierOnClick(event: ClickEvent) {
        const assignButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        this.showAssignCarrierSlideout(tableRow, assignButton);
    }

    buttonOfferOnClick(event: ClickEvent) {
        const offerButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const contactDefaults = this.textboxDefaultContact.isEmpty() ? null : {
            name: this.textboxDefaultContact.text,
            phone: this.textboxPhone.text,
            email: this.textboxEmail.text
        };
        this.showOfferOrderSlideout(tableRow, offerButton, contactDefaults);
    }

    async buttonBlastOfferOnClick(event: ClickEvent) {
        const lastSearch = this.mainDataSource.lastSearch;
        if (lastSearch == null) {
            Snackbar.showWarningSnackbar("No search has been performed yet.");
            return;
        }
        const progressSnackbar = Snackbar.showDownloadSnackbar("Sending...", "Sending Blast Offers email to selected Carrier.", { persist: true });
        const contactDefaults = this.textboxDefaultContact.isEmpty() ? null : {
            name: this.textboxDefaultContact.text,
            phone: this.textboxPhone.text,
            email: this.textboxEmail.text
        };
        const loads = await Promise.all(this.tableAvailableLoads.selectedRows.map(async (row) => {
            const offerRow = new ModelRow("lme/powerbroker/carrier-offer");
            await this.populateOfferFromLoad(row.data, offerRow, contactDefaults);
            offerRow.set("electronic_offer", "Y");
            offerRow.set("disable_electronic_offers", false);
            offerRow.set("source", "BLAST");
            return offerRow;
        }));
        Api.post(this.mainDataSource.url, {
            ...lastSearch.data,
            "type":"BLAST",
            "email": this.textboxEmail.text,
            "loads": loads
        }).then((response) => {
            Toast.showSuccessToast("Success", "Your selected results have been sent to the carrier.");
            this.tableAvailableLoads.selectedRows.forEach(this.toggleOffered);
            this.tableAvailableLoads.selectedRows = [];
        }).finally(() => progressSnackbar.dismiss());
    }

    buttonSendAvailableEmailOnClick(event: ClickEvent) {
        const lastSearch = this.mainDataSource.lastSearch;
        if (lastSearch == null) {
            Snackbar.showWarningSnackbar("No search has been performed yet.");
            return;
        }
        const progressSnackbar = Snackbar.showDownloadSnackbar("Sending...", "Sending Available Loads email to selected Carrier.", { persist: true });
        Api.post(this.mainDataSource.url, {...lastSearch.data, "type":"AVAIL"}).then((response) => {
            Toast.showSuccessToast("Success", "Your search results have been sent to the selected carrier.");
        }).finally(() => progressSnackbar.dismiss());
    }

    labelOrderOnClick(event: ClickEvent) {
        const label = event.target as Label;
        OrderTabView.navigateTo({ key: label.text, newTab: true });
    }

    textboxDefaultContactBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.carrierId == null) {
            event.preventDefault();
            return;
        }
    }

    textboxDefaultContactOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const contact = event.selection;
            this.textboxPhone.text = contact.getString('phone', null);
            this.textboxEmail.text = contact.getString('email', null);
        } else if (event.hasDeselection()) {
            this.textboxPhone.clear();
            this.textboxEmail.clear();
        }
        this.buttonBlastOffer.enabled = !this.textboxEmail.isEmpty() && this.tableAvailableLoads.selectedRows.length > 0;
    }

    textboxCarrierOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const carrier = event.selection;
            this.carrierId = carrier.getString("id", null);
            this.textboxDefaultContact.text = carrier.getString("contact.name", null);
            this.textboxPhone.text = carrier.getString("contact.phone", null);
            this.textboxEmail.text = carrier.getString("contact.email", null);
        } else if (event.hasDeselection()) {
            this._carrierId = null;
            this.carrier = null;
            this.removeContactSearchListenerIfNeeded();
            this.textboxDefaultContact.clear();
            this.textboxPhone.clear();
            this.textboxEmail.clear();
            this.tableAvailableLoads.clearRows();
        }
        this.buttonBlastOffer.enabled = !this.textboxEmail.isEmpty() && this.tableAvailableLoads.selectedRows.length > 0;
    }

    textboxEmailOnBlur(event: BlurEvent) {
        this.buttonBlastOffer.enabled = !this.textboxEmail.isEmpty() && this.tableAvailableLoads.selectedRows.length > 0;
    }

    tableAvailableLoadsOnSelect(event: TableRowDisplayEvent) {
        this.buttonBlastOffer.enabled = !this.textboxEmail.isEmpty() && this.tableAvailableLoads.selectedRows.length > 0;
    }

    rightAlignTextboxCaptionOnPrintable(event: PrintableEvent) {
        const textbox = event.target as Textbox;
        textbox.captionProps = {
            ...textbox.captionProps,
            align: HorizontalAlignment.RIGHT
        };
    }

    buildContactTooltip(labelContacts: Label, contactList: any[]) {
        if (labelContacts != null) {
            const tooltipPanel = new Panel({ id: "tooltipPanel", width: 400 });
            const table = new Table({ headerVisible: false });
            table.addColumn(new TableColumn({ headingDef: { caption: "Name", width: 150, paddingLeft: 4 }, cell: { field: "name" } }), true, false);
            table.addColumn(new TableColumn({ headingDef: { caption: "Email", width: 200, paddingLeft: 4 }, cell: { field: "email" } }), false, true);
            for (const contact of contactList) {
                table.addRow({ name: contact.contact_name, email: contact.email }, null, { display: true, addToData: false });
            }
            tooltipPanel.add(table);
            labelContacts.tooltipDynamicContent = tooltipPanel;
        }
    }

    async showAssignCarrierSlideout(tableRow: TableRow, button: Button) {
        const modelRow = tableRow.data as ModelRow;
        const carrierOfferInfo: CarrierOfferInfo = {
            payee_id: this.mainDataSource.lastSearch?.carrier_id,
            carrier_name: this.mainDataSource.lastSearch ? this.carrier.get("payee.name", null) : null,
            icc_number: this.mainDataSource.lastSearch ? this.carrier.get("icc_number", null) : null,
            dot_number: this.mainDataSource.lastSearch ? this.carrier.get("dot_number", null) : null,
            carrier_contact: this.textboxDefaultContact.text,
            carrier_phone: this.textboxPhone.text,
            carrier_email: this.textboxEmail.text,
        }

        CarrierAssignmentStepper.showAsSlideout({
            movementId: modelRow.get("movement"),
            orderId: modelRow.get("order"),
            carrierAssignmentButton: button,
            carrierOfferInfo,
            onClose: (canceled: boolean) => {
                if (!canceled) {
                    tableRow.table.removeRow(tableRow.data);
                }
            }
        });
    }

    async showOfferOrderSlideout(tableRow: TableRow, offerButton: Button, contactDefaults: { name: string, phone: string, email: string } | null) {
        offerButton.busy = true;
        //Logic segmented by Sections in the Quick Offer Slideout to increase maintainability
        const loCarrierOffer = Layout.getLayout("lme/dispatch/QuickOfferSlideout") as QuickOfferSlideout;
        const rowDecorator = new EditRowDecorator({
            layout: loCarrierOffer,
            dataSource: loCarrierOffer.mainDataSource,
            overlayProps: { greyedBackground: true },
            onSave: (offerRow: ModelRow) => {
                offerRow.post();
                this.toggleOffered(tableRow);
            },
            layoutLoadListeners: async _ => {
                loCarrierOffer.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await loCarrierOffer.mainDataSource.createBlankRow()]);
                rowDecorator.data = loCarrierOffer.activeRow;

                // Decorator Setup
                const orderId = tableRow.data.get("order");
                const movementId = tableRow.data.get("movement");
                rowDecorator.labelTitle.text = `Create Quick Offer - Order ${orderId}`;

                // Initial Data
                await this.populateOfferFromLoad(tableRow.data, loCarrierOffer.activeRow, contactDefaults);

                // QuickSellDetails
                const quickSellDetails = loCarrierOffer.layoutQuickSellDetails as QuickSellDetails;
                quickSellDetails.mainDataSource.search({ id: movementId });

                // General Information
                loCarrierOffer.buttonAssignCarrier.visible = false;

                // Carrier Information
                loCarrierOffer.tbMcNumber.enabled = false;
                loCarrierOffer.tbCarrierName.enabled = false;
                loCarrierOffer.tbDotNumber.enabled = false;
                const carrierId = this.carrier.get("id", "");
                const carrierName = this.carrier.get("payee.name", "");
                const carrierContact = (contactDefaults?.name) ? contactDefaults.name : this.carrier.get("contact.name", "");

                loCarrierOffer.activeRow.setLookupModelData("payee_id", new ModelRow(loCarrierOffer.tbCarrierName.lookupModel, false, {
                    "name": carrierName,
                    "id": carrierId
                }));
                loCarrierOffer.activeRow.setLookupModelData("carrier_name", new ModelRow(loCarrierOffer.tbCarrierName.lookupModel, false, {
                    "name": carrierName,
                    "id": carrierId
                }));
                loCarrierOffer.activeRow.setLookupModelData("equipment_type_id", new ModelRow(loCarrierOffer.tbTrailerType.lookupModel, false, {
                    "id": tableRow.data.get("equipment", null)
                }));

                //Carrier Contact
                loCarrierOffer.activeRow.setLookupModelData("contact_name", new ModelRow(loCarrierOffer.tbContactName.lookupModel, false, { "name": carrierContact }));

                //Rates
                const loCarrierPay = loCarrierOffer.loCarrierPay as MovementCarrierPay;
                loCarrierPay.buttonOverrideTargetPay.enabled = false;
                loCarrierPay.buttonOverrideMaxPay.enabled = false;
                await loCarrierPay.mainDataSource.search({ id: movementId });
                loCarrierPay.displayCalculatedFields();
                loCarrierPay.layoutTargetPay.setupMpact(tableRow.data);
                const targetPay = loCarrierPay.layoutTargetPay.activeRow.getCurrency("target_pay_calc");
                loCarrierOffer.activeRow.setValues({
                    "order_total_chg": loCarrierPay.activeRow.getCurrency("orders.total_charge"),
                    "max_pay": loCarrierPay.activeRow.getCurrency("max_pay_calc"),
                    "target_pay": targetPay
                });

                if (carrierId != null) {
                    await this.calculateCarrierRate(loCarrierOffer.activeRow, carrierId, movementId, orderId);
                }
                if (!loCarrierOffer.activeRow.isNull("amount")) {
                    loCarrierOffer.labelAutoRated.visible = true;
                    CommonTooltips.setTooltipContentLayout(
                        loCarrierOffer.labelAutoRated,
                        loCarrierOffer.activeRow.get("carrier_rate_lane_id", null),
                        "lme/dispatch/rates/CarrierLaneRateQuickInfo"
                    );
                } else {
                    loCarrierOffer.activeRow.set("amount", targetPay);
                }

                loCarrierOffer.updateMargins(OfferField.OFFER, "offer_margin", loCarrierOffer.tbDispatchMargin, loCarrierOffer.tbDispatchMarginPrcnt, true);

                loCarrierOffer.mainDataSource.displayDataInBoundComponents();
                offerButton.busy = false;
            }
        });
    }

    private toggleOffered(tableRow: TableRow) {
        const buttonOffer = tableRow.findComponentById("buttonOffer") as Button;
        if (buttonOffer) {
            buttonOffer.visible = false;
        }
        const imageOffered = tableRow.findComponentById("imageOffered");
        if (imageOffered) {
            imageOffered.visible = true;
        }
    }

    private async populateOfferFromLoad(source: ModelRow, target: ModelRow, contactDefaults?: { name: string, phone: string, email: string }) {
        const orderId = source.get("order");
        const movementId = source.get("movement");
        const carrierId = this.carrier.get("id", "");
        const carrierName = this.carrier.get("payee.name", "");
        const carrierContact = (contactDefaults) ? contactDefaults.name : this.carrier.get("contact.name", "");

        target._appending = true;
        target.setValues({
            "order_id": orderId,
            "payee_id": carrierId,
            "icc_number": this.carrier.get("icc_number", ""),
            "dot_number": this.carrier.get("dot_number", ""),
            "movement_id": movementId,
            "carrier_name": carrierName,
            "contact_phone": (contactDefaults) ? contactDefaults.phone : this.carrier.get("contact.phone", ""),
            "allow_cntr_offer": true,
            "contact_name": carrierContact,
            "max_pay": source.getCurrency("max_pay"),
            "target_pay": source.getCurrency("target_pay"),
            "email": (contactDefaults) ? contactDefaults.email : this.carrier.get("contact.email", ""),
            "offer_date": new Date(),
            "entered_user_id": LMEUserSettings.getUserId(),
            "source": OfferSource.QUICKOFFER,
            "status": "I",
            "electronic_offer": "N",
            "my_avail_offers": "Y",
            "orders_on_hold": source.get("on_hold", null),
            "equipment_type_id": source.get("equipment", null),
        });

        if (carrierId != null) {
            await this.calculateCarrierRate(target, carrierId, movementId, orderId);
        }
        if (target.isNull("amount")) {
            target.set("amount", target.getCurrency("target_pay"));
        }
    }

    async calculateCarrierRate(modelRow: ModelRow, payeeId: string, movementId: string, orderId: string) {
        const response = await Api.search("lme/dispatch/calculate-override-pay", {
            "movement_id": movementId,
            "order_id": orderId,
            "payee_id": payeeId,
            "auto_rate": LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y",
            "calculate_fuel_surcharge": true
        });
        if (response.data[0]?.carrier_rate_lane_id) {
            //Had to create new currency for each because of passing by reference issues
            const carrier_rate = new Currency(response.data[0]?.total_pay);
            modelRow.set("amount", carrier_rate);
            modelRow.set("carrier_rate", carrier_rate);
            modelRow.set("carrier_rate_calc_pay", carrier_rate);
            modelRow.set("carrier_rate_id", response.data[0]?.carrier_rate_id);
            modelRow.set("carrier_rate_lane_id", response.data[0]?.carrier_rate_lane_id);
        } else {
            modelRow.set("carrier_rate", null);
        }
    }

    private initializeContactFilter(carrierId: string) {
        this.removeContactSearchListenerIfNeeded();
        this._contactFilter = (event: LookupModelSearchEvent) => {
            event.filter.is_active = 'Y';
            event.filter.parent_row_type = 'P';
            event.filter.parent_row_id = carrierId;
        };
        this.textboxDefaultContact.addBeforeLookupModelSearchListener(this._contactFilter);
    }

    private removeContactSearchListenerIfNeeded() {
        if (this._contactFilter) {
            this.textboxDefaultContact.removeBeforeLookupModelSearchListener(this._contactFilter);
        }
    }

    get carrierId() {
        return this._carrierId;
    }

    set carrierId(value: string) {
        this._carrierId = value;
        if (this.carrierId) {
            this.initializeContactFilter(this.carrierId);
            new ModelCarrier().searchSingle({ id: this.carrierId }, "lme/dispatch/Carrier").then((carrierRow) => {
                this.carrier = carrierRow as RowCarrier;
                this.mainDataSource.searchRow.setValueAndLookupModelData("carrier_id", this.carrierId, { id: this.carrierId, name: carrierRow.get("payee.name") });
                this.textboxCarrier.displayComponentData(this.mainDataSource.searchRow, null, null);
                this.mainDataSource.executeValidatedSearch();
            });
        }
    }
}
